/**
 * Configuration Optimizer - Optimizes the configuration after shape search
 *
 * This handles configuration-level optimization which creates NEW candidates
 * from the best candidate found during shape search. This is different from
 * candidate-level adjustments which happen during shape search.
 *
 * Key features:
 * - Only runs if unlimited bonds exist (targeted-only has no room for improvement)
 * - Creates new optimized candidates (doesn't modify existing ones)
 * - Handles utilization optimization and compliance checks
 */

import { DRF_LIMITS } from '~/config/financial-constants';
import { logger } from '~/services/logging';
import type { Candidate, Configuration, Project } from '~/types';
import type { FiveYearRuleOptimizationConfig } from '../../types';
import { OptimizationType } from '../../types';
import type { DRFRecoveryConfig, RecoveryResult } from './drf-recovery-optimizer';
import { DRFRecoveryOptimizer } from './drf-recovery-optimizer';
import { FiveYearRuleOptimizer } from './five-year-rule-optimizer';
import type { MillageRedistributionConfig } from './millage-redistribution-optimizer';
import { MillageRedistributionOptimizer } from './millage-redistribution-optimizer';
import type {
  UtilizationOrchestrationConfig,
  UtilizationOrchestrationResult,
} from './utilization-orchestrator';
import { UtilizationOrchestrator } from './utilization-orchestrator';

/**
 * Analysis result for an optimization
 */
export interface OptimizationAnalysis {
  type: OptimizationType;
  shouldOptimize: boolean;
  reason: string;
  severity?: 'high' | 'medium' | 'low';
  estimatedImpact?: string;
}

/**
 * Result of a pipeline optimization
 */
export interface PipelineOptimizationResult {
  success: boolean;
  originalCandidate: Candidate;
  finalCandidate: Candidate;
  optimizationsApplied: OptimizationType[];
  allGeneratedCandidates: Candidate[];
  analyses: OptimizationAnalysis[];
  totalProcessingTime: number;
  error?: string;
}

/**
 * Configuration for pipeline optimization
 */
export interface PipelineOptimizationConfig {
  // Which optimizations to consider
  enabledOptimizations?: OptimizationType[];

  // Whether to run all optimizations or stop on first success
  runAll?: boolean;

  // Skip analysis and run all enabled optimizations
  skipAnalysis?: boolean;

  // Individual optimizer configs
  utilizationConfig?: Partial<UtilizationOrchestrationConfig>;
  fiveYearRuleConfig?: FiveYearRuleOptimizationConfig;
  millageRedistributionConfig?: Partial<MillageRedistributionConfig>;
  project?: Project;
  configuration?: Configuration;

  // Logging
  enableLogging?: boolean;
}

/**
 * Feature flags for controlling which optimizations run
 */
export interface OptimizationFeatureFlags {
  ENABLE_FIVE_YEAR_RULE?: boolean;
  ENABLE_UTILIZATION?: boolean;
  ENABLE_MILLAGE_REDISTRIBUTION?: boolean;
}

/**
 * Default feature flags - all optimizations enabled
 */
export const DEFAULT_FEATURE_FLAGS: OptimizationFeatureFlags = {
  ENABLE_FIVE_YEAR_RULE: true,
  ENABLE_UTILIZATION: true,
  ENABLE_MILLAGE_REDISTRIBUTION: true,
};

/**
 * Options for the unified optimization pipeline entry point
 */
export interface OptimizationPipelineOptions {
  featureFlags?: OptimizationFeatureFlags;
  enableLogging?: boolean;
  utilizationConfig?: Partial<UtilizationOrchestrationConfig>;
  fiveYearRuleConfig?: FiveYearRuleOptimizationConfig;
  millageRedistributionConfig?: Partial<MillageRedistributionConfig>;
}

export class ConfigurationOptimizer {
  /**
   * Default optimization sequence - fixed order
   * 1. FIVE_YEAR_RULE - Regulatory compliance first
   * 2. UTILIZATION - DRF efficiency
   * 3. MILLAGE_REDISTRIBUTION - Shape refinement last
   */
  private static readonly DEFAULT_OPTIMIZATION_SEQUENCE: OptimizationType[] = [
    OptimizationType.FIVE_YEAR_RULE,
    OptimizationType.UTILIZATION,
    OptimizationType.MILLAGE_REDISTRIBUTION,
  ];

  /**
   * Unified optimization pipeline entry point
   *
   * This is the single entry point for all optimization. Use this method
   * instead of optimizeFull() for consistent behavior across all call sites.
   *
   * @param candidate - The candidate to optimize (must be viable)
   * @param project - The project context
   * @param configuration - The bond configuration
   * @param options - Optional feature flags and optimizer configs
   *
   * @example
   * // Run all optimizations with defaults
   * const result = ConfigurationOptimizer.runOptimizationPipeline(
   *   candidate, project, configuration
   * );
   *
   * // Disable specific optimizations
   * const result = ConfigurationOptimizer.runOptimizationPipeline(
   *   candidate, project, configuration,
   *   { featureFlags: { ENABLE_MILLAGE_REDISTRIBUTION: false } }
   * );
   */
  static runOptimizationPipeline(
    candidate: Candidate,
    project: Project,
    configuration: Configuration,
    options: OptimizationPipelineOptions = {}
  ): PipelineOptimizationResult {
    const { featureFlags = DEFAULT_FEATURE_FLAGS, enableLogging = true } = options;

    // Log pipeline start
    if (enableLogging) {
      logger.L3.debug(`[OPT PIPELINE] Starting optimization pipeline for ${candidate.name}`);
    }

    // Build sequence from feature flags
    const sequence = ConfigurationOptimizer.buildSequenceFromFlags(featureFlags);

    if (enableLogging) {
      logger.L3.debug(
        `[OPT PIPELINE] Sequence: ${sequence.length > 0 ? sequence.join(' → ') : 'none (all disabled)'}`
      );
    }

    // If no optimizations enabled, return early
    if (sequence.length === 0) {
      if (enableLogging) {
        logger.L3.debug('[OPT PIPELINE] No optimizations enabled, skipping pipeline');
      }
      return {
        success: false,
        originalCandidate: candidate,
        finalCandidate: candidate,
        optimizationsApplied: [],
        allGeneratedCandidates: [],
        analyses: [],
        totalProcessingTime: 0,
        error: 'No optimizations enabled via feature flags',
      };
    }

    // Run the optimization pipeline
    const result = ConfigurationOptimizer.optimizeFull(candidate, {
      enabledOptimizations: sequence,
      runAll: true,
      skipAnalysis: false,
      project,
      configuration,
      enableLogging,
      utilizationConfig: options.utilizationConfig,
      fiveYearRuleConfig: options.fiveYearRuleConfig,
      millageRedistributionConfig: options.millageRedistributionConfig,
    });

    // Log pipeline completion
    if (enableLogging) {
      if (result.success) {
        logger.L3.debug(
          `[OPT PIPELINE] Complete - Applied: ${result.optimizationsApplied.join(', ')}, ` +
            `Generated ${result.allGeneratedCandidates.length} candidate(s), ` +
            `Time: ${result.totalProcessingTime}ms`
        );
      } else {
        logger.L3.debug(
          `[OPT PIPELINE] Complete - No optimizations applied, Time: ${result.totalProcessingTime}ms`
        );
      }
    }

    return result;
  }

  /**
   * Build optimization sequence from feature flags
   */
  private static buildSequenceFromFlags(flags: OptimizationFeatureFlags): OptimizationType[] {
    const sequence: OptimizationType[] = [];

    // Add in the correct order based on DEFAULT_OPTIMIZATION_SEQUENCE
    if (flags.ENABLE_FIVE_YEAR_RULE !== false) {
      sequence.push(OptimizationType.FIVE_YEAR_RULE);
    }
    if (flags.ENABLE_UTILIZATION !== false) {
      sequence.push(OptimizationType.UTILIZATION);
    }
    if (flags.ENABLE_MILLAGE_REDISTRIBUTION !== false) {
      sequence.push(OptimizationType.MILLAGE_REDISTRIBUTION);
    }

    return sequence;
  }

  /**
   * Run full optimization suite with smart analysis
   *
   * @deprecated Use runOptimizationPipeline() instead for consistent behavior.
   * This method is kept for internal use and backward compatibility during migration.
   */
  static optimizeFull(
    candidate: Candidate,
    config: PipelineOptimizationConfig = {}
  ): PipelineOptimizationResult {
    const startTime = Date.now();
    const {
      enabledOptimizations = ConfigurationOptimizer.DEFAULT_OPTIMIZATION_SEQUENCE,
      runAll = true,
      skipAnalysis = false,
      enableLogging = true, // Enable logging by default for debugging
    } = config;

    if (enableLogging) {
      logger.L3.debug(
        `🚀 CONFIGURATION OPTIMIZATION: Starting full optimization for ${candidate.name}`
      );
    }

    // Validate candidate
    if (!candidate.results?.isViable) {
      return {
        success: false,
        originalCandidate: candidate,
        finalCandidate: candidate,
        optimizationsApplied: [],
        allGeneratedCandidates: [],
        analyses: [],
        totalProcessingTime: Date.now() - startTime,
        error: 'Cannot optimize non-viable candidate',
      };
    }

    const analyses: OptimizationAnalysis[] = [];
    const optimizationsApplied: OptimizationType[] = [];
    const allGeneratedCandidates: Candidate[] = [];
    let currentCandidate = candidate;

    // Step 1: Analyze all optimizations (unless skipped)
    if (!skipAnalysis) {
      for (const optimizationType of enabledOptimizations) {
        const analysis = ConfigurationOptimizer.analyzeOptimization(
          currentCandidate,
          optimizationType,
          config
        );
        analyses.push(analysis);

        if (enableLogging) {
          logger.L3.debug(
            `📊 Analysis - ${optimizationType}: ${analysis.shouldOptimize ? '✅' : '❌'} ${analysis.reason}`
          );
        }
      }
    }

    // Step 2: Execute optimizations based on analysis
    for (const optimizationType of enabledOptimizations) {
      // Check if we should run this optimization
      const shouldRun =
        skipAnalysis || analyses.find((a) => a.type === optimizationType)?.shouldOptimize;

      if (!shouldRun) {
        continue;
      }

      if (enableLogging) {
        logger.L3.debug(`🔧 Executing ${optimizationType} optimization...`);
      }

      const result = ConfigurationOptimizer.executeOptimization(
        currentCandidate,
        optimizationType,
        config
      );

      if (result.success && result.candidates.length > 0) {
        currentCandidate = result.finalCandidate;
        optimizationsApplied.push(optimizationType);
        allGeneratedCandidates.push(...result.candidates);

        if (enableLogging) {
          logger.L3.trace(
            `✅ ${optimizationType} optimization completed: ${result.candidates.length} candidates generated`,
            {
              context: optimizationType,
            }
          );
        }

        // Stop if configured to stop on first success
        if (!runAll) {
          break;
        }
      } else if (enableLogging) {
        logger.L3.debug(
          `⏭️  ${optimizationType} optimization skipped or failed: ${result.error || 'No improvements found'}`
        );
      }
    }

    const totalProcessingTime = Date.now() - startTime;

    // Determine the final candidate
    const finalCandidate =
      allGeneratedCandidates.length > 0
        ? allGeneratedCandidates[allGeneratedCandidates.length - 1]
        : candidate;

    // Add skipped optimization entries to the final candidate's history
    // This provides transparency about which optimizations were checked but not needed
    if (!skipAnalysis && analyses.length > 0) {
      // Ensure optimizationHistory exists
      if (!finalCandidate.optimizationHistory) {
        finalCandidate.optimizationHistory = [];
      }

      // Add entries for skipped optimizations (those analyzed but not applied)
      for (const analysis of analyses) {
        if (!analysis.shouldOptimize) {
          finalCandidate.optimizationHistory.push({
            type: 'optimization-skipped',
            description: `${ConfigurationOptimizer.getOptimizationDisplayName(analysis.type)}: ${analysis.reason}`,
            timestamp: new Date().toISOString(),
            details: {
              optimizationType: analysis.type,
              reason: analysis.reason,
            },
          });
        }
      }
    }

    if (enableLogging) {
      logger.L3.debug(`🏁 CONFIGURATION OPTIMIZATION: Completed in ${totalProcessingTime}ms`, {
        optimizationsApplied: optimizationsApplied.join(', ') || 'none',
        totalCandidates: allGeneratedCandidates.length,
      });
    }

    return {
      success: optimizationsApplied.length > 0,
      originalCandidate: candidate,
      finalCandidate,
      optimizationsApplied,
      allGeneratedCandidates,
      analyses,
      totalProcessingTime,
    };
  }

  /**
   * Get human-readable display name for optimization type
   */
  private static getOptimizationDisplayName(type: OptimizationType): string {
    switch (type) {
      case OptimizationType.UTILIZATION:
        return 'Utilization optimization';
      case OptimizationType.FIVE_YEAR_RULE:
        return 'Five-year rule check';
      case OptimizationType.MILLAGE_REDISTRIBUTION:
        return 'Millage redistribution';
      default:
        return type;
    }
  }

  /**
   * Run utilization optimization only
   */
  static optimizeUtilization(
    candidate: Candidate,
    project: Project,
    configuration: Configuration,
    config: UtilizationOrchestrationConfig = {}
  ): UtilizationOrchestrationResult {
    const orchestrator = new UtilizationOrchestrator({
      ...config,
    });

    return orchestrator.optimize(candidate, project, configuration);
  }

  /**
   * Run five-year rule optimization only
   */
  static optimizeFiveYearRule(
    candidate: Candidate,
    configuration: Configuration,
    config?: FiveYearRuleOptimizationConfig
  ): {
    success: boolean;
    originalCandidate: Candidate;
    finalCandidate: Candidate;
    description: string;
  } {
    const optimizer = new FiveYearRuleOptimizer({
      ...config,
    });

    const optimizedCandidate = optimizer.optimize(candidate, configuration);
    const wasOptimized = optimizedCandidate !== candidate;

    return {
      success: wasOptimized,
      originalCandidate: candidate,
      finalCandidate: optimizedCandidate,
      description: wasOptimized
        ? 'Applied five-year rule compliance optimization'
        : 'Five-year rule optimization not needed',
    };
  }

  /**
   * Run millage redistribution optimization only
   */
  static optimizeMillageRedistribution(
    candidate: Candidate,
    project: Project,
    configuration: Configuration,
    config?: Partial<MillageRedistributionConfig>
  ): {
    success: boolean;
    originalCandidate: Candidate;
    finalCandidate: Candidate;
    description: string;
  } {
    const result = MillageRedistributionOptimizer.optimize(
      candidate,
      project,
      configuration,
      config
    );

    return {
      success: result.success,
      originalCandidate: candidate,
      finalCandidate: result.optimizedCandidate || candidate,
      description: result.message,
    };
  }

  /**
   * Run a custom sequence of optimizations
   */
  static optimizeSequence(
    candidate: Candidate,
    sequence: OptimizationType[],
    config: PipelineOptimizationConfig = {}
  ): PipelineOptimizationResult {
    return ConfigurationOptimizer.optimizeFull(candidate, {
      ...config,
      enabledOptimizations: sequence,
      skipAnalysis: true, // Custom sequences skip analysis by default
    });
  }

  /**
   * Attempt to recover a non-viable candidate with negative DRF
   *
   * This is a separate entry point from the optimization pipeline because:
   * 1. It runs on NON-VIABLE candidates (opposite of runOptimizationPipeline)
   * 2. It attempts to "rescue" a failed candidate by reducing principal
   * 3. If successful, the recovered candidate can then go through normal optimization
   *
   * @param candidate - The non-viable candidate to recover (must have negative DRF)
   * @param configuration - The bond configuration
   * @param config - Optional recovery configuration
   *
   * @example
   * // Attempt recovery on a failed candidate
   * const result = ConfigurationOptimizer.attemptRecovery(
   *   failedCandidate,
   *   configuration
   * );
   *
   * if (result.success && result.recoveredCandidate) {
   *   // Now run normal optimization on the recovered candidate
   *   const optimized = ConfigurationOptimizer.runOptimizationPipeline(
   *     result.recoveredCandidate,
   *     project,
   *     configuration
   *   );
   * }
   */
  static attemptRecovery(
    candidate: Candidate,
    configuration: Configuration,
    config: DRFRecoveryConfig = {}
  ): RecoveryResult {
    const enableLogging = config.enableLogging ?? true;

    if (enableLogging) {
      logger.L3.debug(`[OPT RECOVERY] Attempting recovery for ${candidate.name}`);
    }

    // Quick check: if candidate is already viable, no recovery needed
    if (candidate.results?.isViable) {
      if (enableLogging) {
        logger.L3.debug('[OPT RECOVERY] Candidate is already viable, no recovery needed');
      }
      return {
        success: false,
        recoveredCandidate: null,
        originalCandidate: candidate,
        iterationsPerformed: 0,
        description: 'Candidate is already viable - use runOptimizationPipeline instead',
        adjustments: [],
      };
    }

    // Delegate to DRFRecoveryOptimizer
    const result = DRFRecoveryOptimizer.attemptRecovery(candidate, configuration, config);

    if (enableLogging) {
      if (result.success) {
        logger.L3.debug(
          `[OPT RECOVERY] Success - recovered after ${result.iterationsPerformed} iteration(s)`
        );
      } else {
        logger.L3.debug(`[OPT RECOVERY] Failed - ${result.description}`);
      }
    }

    return result;
  }

  /**
   * Analyze whether an optimization should be applied
   */
  private static analyzeOptimization(
    candidate: Candidate,
    optimizationType: OptimizationType,
    config: PipelineOptimizationConfig
  ): OptimizationAnalysis {
    switch (optimizationType) {
      case OptimizationType.UTILIZATION: {
        // Quick check for DRF imbalance
        const firstCashFlow = candidate.cashFlows?.[0];
        const lastCashFlow = candidate.cashFlows?.[candidate.cashFlows.length - 1];

        if (!firstCashFlow || !lastCashFlow) {
          return {
            type: optimizationType,
            shouldOptimize: false,
            reason: 'Missing cash flow data',
          };
        }

        const imbalance = Math.abs(
          lastCashFlow.drfEndingBalance - firstCashFlow.drfStartingBalance
        );
        const threshold =
          config.utilizationConfig?.drfBufferThreshold || DRF_LIMITS.DRF_BUFFER_THRESHOLD;

        if (imbalance > threshold) {
          return {
            type: optimizationType,
            shouldOptimize: true,
            reason: `DRF imbalance of $${imbalance.toLocaleString()} exceeds $${threshold.toLocaleString()} threshold`,
            severity:
              imbalance > threshold * 10 ? 'high' : imbalance > threshold * 5 ? 'medium' : 'low',
            estimatedImpact: `Could reduce ending DRF by ~$${imbalance.toLocaleString()}`,
          };
        }

        return {
          type: optimizationType,
          shouldOptimize: false,
          reason: 'DRF balance is already well optimized',
        };
      }

      case OptimizationType.FIVE_YEAR_RULE: {
        // For now, always check five-year rule compliance
        // In the future, we could do a quick pre-check here
        return {
          type: optimizationType,
          shouldOptimize: true,
          reason: 'Five-year rule compliance check required for all candidates',
          severity: 'high',
        };
      }

      case OptimizationType.MILLAGE_REDISTRIBUTION: {
        // Check if millage redistribution would help
        const analysis = MillageRedistributionOptimizer.detectOptimizationNeed(candidate);
        if (!analysis.shouldOptimize) {
          return {
            type: optimizationType,
            shouldOptimize: false,
            reason: analysis.reason,
          };
        }

        const severity =
          analysis.utilizationPercentage < 90
            ? 'high'
            : analysis.utilizationPercentage < 95
              ? 'medium'
              : 'low';

        return {
          type: optimizationType,
          shouldOptimize: true,
          reason: analysis.reason,
          severity,
          estimatedImpact: `Could improve utilization from ${analysis.utilizationPercentage.toFixed(2)}% to >99%`,
        };
      }

      case OptimizationType.COMBINED: {
        // Combined optimization runs both utilization and five-year rule
        // This is handled by running the full pipeline with both optimizations
        return {
          type: optimizationType,
          shouldOptimize: false,
          reason: 'Combined optimization is handled by running full pipeline',
        };
      }

      default:
        return {
          type: optimizationType,
          shouldOptimize: false,
          reason: `Unknown optimization type: ${optimizationType}`,
        };
    }
  }

  /**
   * Execute a specific optimization
   */
  private static executeOptimization(
    candidate: Candidate,
    optimizationType: OptimizationType,
    config: PipelineOptimizationConfig
  ): {
    success: boolean;
    finalCandidate: Candidate;
    candidates: Candidate[];
    error?: string;
  } {
    try {
      switch (optimizationType) {
        case OptimizationType.UTILIZATION: {
          // Ensure enableLogging is passed through to utilization config
          const utilizationConfig = {
            ...config.utilizationConfig,
            enableLogging: config.enableLogging ?? true,
          };
          if (!config.project || !config.configuration) {
            throw new Error('Project and configuration are required for utilization optimization');
          }
          const result = ConfigurationOptimizer.optimizeUtilization(
            candidate,
            config.project,
            config.configuration,
            utilizationConfig
          );
          return {
            success: result.wasOptimized,
            finalCandidate: result.finalCandidate,
            candidates: result.allIterationCandidates,
          };
        }

        case OptimizationType.FIVE_YEAR_RULE: {
          // Ensure enableLogging is passed through to five-year rule config
          const fiveYearConfig = {
            ...config.fiveYearRuleConfig,
            enableLogging: config.enableLogging ?? true,
          };
          if (!config.configuration) {
            throw new Error('Configuration is required for five-year rule optimization');
          }
          const result = ConfigurationOptimizer.optimizeFiveYearRule(
            candidate,
            config.configuration,
            fiveYearConfig
          );
          return {
            success: result.success,
            finalCandidate: result.finalCandidate,
            candidates: result.success ? [result.finalCandidate] : [],
          };
        }

        case OptimizationType.MILLAGE_REDISTRIBUTION: {
          const millageConfig = {
            ...config.millageRedistributionConfig,
          };
          if (!config.project || !config.configuration) {
            throw new Error(
              'Project and configuration are required for millage redistribution optimization'
            );
          }
          const result = ConfigurationOptimizer.optimizeMillageRedistribution(
            candidate,
            config.project,
            config.configuration,
            millageConfig
          );
          return {
            success: result.success,
            finalCandidate: result.finalCandidate,
            candidates: result.success ? [result.finalCandidate] : [],
          };
        }

        default:
          return {
            success: false,
            finalCandidate: candidate,
            candidates: [],
            error: `Unknown optimization type: ${optimizationType}`,
          };
      }
    } catch (error) {
      logger.error(
        `❌ Error executing ${optimizationType} optimization:`,
        error instanceof Error ? error : undefined
      );
      return {
        success: false,
        finalCandidate: candidate,
        candidates: [],
        error: error instanceof Error ? error.message : String(error),
      };
    }
  }
}
